import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import usePageMeta from "../hooks/usePageMeta";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import ApplicationModal from "../components/ApplicationModal";
import KanbanBoard from "../components/KanbanBoard";
import ParseJDModal from "../components/ParseJDModal";
import CoverLetterModal from "../components/CoverLetterModal";
import ShareModal from "../components/ShareModal";
import ResumeImportModal from "../components/ResumeImportModal";
import WelcomeTour, { DEFAULT_TOUR_STEPS, PRO_UNLOCK_TOUR_STEPS } from "../components/WelcomeTour";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Plus, Download, Search, LogOut, Pencil, Trash2, ExternalLink, Check, X,
  MoreHorizontal, ChevronDown, ChevronUp, ArrowUpDown, LayoutGrid, Table as TableIcon,
  Upload, CalendarClock, AlertCircle, Mail, BarChart3, Lock, Zap, Sparkles, FileText, Share2,
} from "lucide-react";

const STATUS_STYLE = {
  Applied: "bg-[#E5E1DB] text-[#5C6B64]",
  Interviewing: "bg-[#FDF2DF] text-[#8B6A1F]",
  Offer: "bg-[#E2F0E7] text-[#1A362D]",
  Rejected: "bg-[#FCECEC] text-[#C65D47]",
};

const SORT_COLS = [
  { key: "company", label: "Company" },
  { key: "job_title", label: "Job title" },
  { key: "website", label: "Source" },
  { key: "pay_range", label: "Pay" },
  { key: "status", label: "Status" },
  { key: "submitted", label: "Submitted" },
  { key: "date_applied", label: "Applied on" },
];

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const target = new Date(dateStr + "T00:00:00");
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((target - today) / (1000 * 60 * 60 * 24));
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [apps, setApps] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [followupList, setFollowupList] = useState([]);
  const [followupOnly, setFollowupOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, applied: 0, interviewing: 0, offer: 0, rejected: 0 });
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [view, setView] = useState("table"); // table | kanban
  const [sortKey, setSortKey] = useState("date_applied");
  const [sortDir, setSortDir] = useState("desc"); // asc | desc
  const [selected, setSelected] = useState(new Set());
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [prefill, setPrefill] = useState(null);
  const [parseJdOpen, setParseJdOpen] = useState(false);
  const [coverLetterFor, setCoverLetterFor] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [resumeOpen, setResumeOpen] = useState(false);
  const [resumeWelcome, setResumeWelcome] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [tourSteps, setTourSteps] = useState(DEFAULT_TOUR_STEPS);
  const [importSummary, setImportSummary] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [importing, setImporting] = useState(false);

  usePageMeta("Dashboard · Trackr", "Your job applications, pipeline, and follow-ups in one place.");

  const load = async () => {
    try {
      const [{ data: a }, { data: s }, { data: u }, { data: f }] = await Promise.all([
        api.get("/applications"),
        api.get("/stats"),
        api.get("/applications/upcoming"),
        api.get("/applications/needs_followup"),
      ]);
      setApps(a);
      setStats(s);
      setUpcoming(u);
      setFollowupList(f);
    } catch (err) {
      if (err.response?.status === 401) navigate("/login");
      else toast.error("Failed to load applications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load();  }, []);

  // One-time "Welcome: upload your resume" prompt for brand-new users with 0 apps.
  useEffect(() => {
    if (loading) return;
    if (!user?.user_id) return;
    if (apps.length > 0) return;
    const key = `trackr.welcomed.${user.user_id}`;
    if (localStorage.getItem(key)) return;
    setResumeWelcome(true);
    setResumeOpen(true);
    localStorage.setItem(key, new Date().toISOString());
  }, [loading, user?.user_id, apps.length]);

  // Pro-nudge tour — fires once when a free user crosses 20 applications.
  // "You're getting serious — here's what else is possible."
  useEffect(() => {
    if (loading) return;
    if (!user?.user_id) return;
    if (user.tier === "pro") return;
    if (apps.length < 20) return;
    const key = `trackr.tour.pro.${user.user_id}`;
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, new Date().toISOString());
    setTourSteps(PRO_UNLOCK_TOUR_STEPS);
    // Short delay so the dashboard settles after load.
    setTimeout(() => setTourOpen(true), 600);
  }, [loading, user?.user_id, user?.tier, apps.length]);

  const followupIds = useMemo(() => new Set(followupList.map((a) => a.id)), [followupList]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let out = apps.filter((a) => {
      if (followupOnly && !followupIds.has(a.id)) return false;
      if (statusFilter !== "All" && a.status !== statusFilter) return false;
      if (!q) return true;
      return (
        a.company.toLowerCase().includes(q) ||
        a.job_title.toLowerCase().includes(q) ||
        (a.website || "").toLowerCase().includes(q) ||
        (a.notes || "").toLowerCase().includes(q) ||
        (a.recruiter_name || "").toLowerCase().includes(q) ||
        (a.hiring_manager || "").toLowerCase().includes(q)
      );
    });
    const dir = sortDir === "asc" ? 1 : -1;
    out.sort((a, b) => {
      const av = a[sortKey] ?? "";
      const bv = b[sortKey] ?? "";
      if (av === bv) return 0;
      if (typeof av === "boolean" || typeof bv === "boolean") return (av === bv ? 0 : av ? 1 : -1) * dir;
      return (av > bv ? 1 : -1) * dir;
    });
    return out;
  }, [apps, search, statusFilter, sortKey, sortDir, followupOnly, followupIds]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  const toggleSelect = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const toggleSelectAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((a) => a.id)));
  };
  const clearSelection = () => setSelected(new Set());

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/applications/${toDelete.id}`);
      toast.success("Application deleted");
      setToDelete(null);
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const handleBulkDelete = async () => {
    try {
      const ids = Array.from(selected);
      const { data } = await api.post("/applications/bulk_delete", { ids });
      toast.success(`Deleted ${data.deleted} application${data.deleted === 1 ? "" : "s"}`);
      clearSelection();
      setConfirmBulk(false);
      load();
    } catch {
      toast.error("Bulk delete failed");
    }
  };

  const handleBulkStatus = async (status) => {
    try {
      const ids = Array.from(selected);
      const { data } = await api.post("/applications/bulk_status", { ids, status });
      toast.success(`Moved ${data.updated} to ${status}`);
      clearSelection();
      load();
    } catch {
      toast.error("Bulk update failed");
    }
  };

  const handleKanbanStatus = async (id, status) => {
    const prev = apps;
    setApps((cur) => cur.map((a) => (a.id === id ? { ...a, status } : a)));
    try {
      await api.patch(`/applications/${id}/status`, { status });
      load();
    } catch {
      setApps(prev);
      toast.error("Failed to update status");
    }
  };

  const doExport = async (fmt) => {
    if (!isPro && (fmt === "xlsx" || fmt === "pdf")) {
      toast.error(`${fmt.toUpperCase()} export is a Trackr Pro feature.`, {
        action: { label: "Upgrade", onClick: () => navigate("/upgrade") },
      });
      return;
    }
    try {
      const res = await api.get(`/export/${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `applications.${fmt === "xlsx" ? "xlsx" : fmt}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${fmt.toUpperCase()}`);
    } catch {
      toast.error("Export failed");
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/import/csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.imported} application${data.imported === 1 ? "" : "s"}`);
      if (data.errors?.length) {
        toast.warning(`Skipped ${data.errors.length} row${data.errors.length === 1 ? "" : "s"}: ${data.errors[0]}`);
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const handleLogout = async () => { await logout(); navigate("/"); };

  const isPro = user?.tier === "pro";
  const atFreeLimit = !isPro && apps.length >= 25;

  const allSelected = filtered.length > 0 && selected.size === filtered.length;
  const someSelected = selected.size > 0 && selected.size < filtered.length;

  const SortHeader = ({ col }) => {
    const active = sortKey === col.key;
    const Icon = active ? (sortDir === "asc" ? ChevronUp : ChevronDown) : ArrowUpDown;
    return (
      <button
        type="button"
        onClick={() => toggleSort(col.key)}
        className={`inline-flex items-center gap-1 hover:text-[#1A362D] transition-colors ${active ? "text-[#1A362D]" : ""}`}
        data-testid={`sort-${col.key}`}
      >
        {col.label} <Icon className="w-3 h-3" />
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-[#F4F1EE]">
      <header className="glass sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-[#1A362D] flex items-center justify-center">
              <span className="text-[#D99C3D] font-display font-bold">T</span>
            </div>
            <span className="font-display text-lg text-[#1A362D]">Trackr</span>
          </div>
          <div className="flex items-center gap-3">
            {isPro ? (
              <span className="hidden sm:inline-flex items-center gap-1 rounded-full bg-[#1A362D] text-[#D99C3D] text-xs tracking-[0.15em] uppercase font-semibold px-3 py-1"
                data-testid="pro-badge">
                <Zap className="w-3 h-3" /> Pro
              </span>
            ) : (
              <Link
                to="/upgrade"
                className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-[#D99C3D] hover:bg-[#C48A2E] text-[#1A362D] text-sm font-semibold px-3.5 py-1.5 transition-colors"
                data-testid="header-upgrade-btn"
              >
                <Zap className="w-3.5 h-3.5" /> Upgrade
              </Link>
            )}
            <Link
              to="/analytics"
              className="hidden md:inline-flex items-center gap-1.5 text-sm rounded-full px-3 py-1.5 text-[#1A362D] hover:bg-[#1A362D]/5 transition-colors"
              data-testid="nav-analytics-link"
            >
              <BarChart3 className="w-4 h-4" /> Analytics
              {!isPro && <Lock className="w-3 h-3 opacity-60" />}
            </Link>
            <Link
              to="/settings"
              className="hidden md:inline-flex items-center gap-1.5 text-sm rounded-full px-3 py-1.5 text-[#1A362D] hover:bg-[#1A362D]/5 transition-colors"
              data-testid="nav-settings-link"
              title="Settings & notifications"
            >
              <Mail className="w-4 h-4" />
            </Link>
            <span className="hidden sm:block text-sm text-[#5C6B64]" data-testid="user-email-display">
              {user?.email}
            </span>
            <Button variant="ghost" size="sm" onClick={handleLogout}
              className="rounded-full" data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-1.5" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-[#5C6B64]">Your search, distilled</p>
            <h1 className="font-display text-4xl sm:text-5xl mt-2 text-[#1A362D]">
              Hi {user?.name?.split(" ")[0] || "there"}.
            </h1>
          </div>
          <div className="flex flex-wrap gap-2">
            <input ref={fileRef} type="file" accept=".csv" onChange={handleImport}
              className="hidden" data-testid="import-file-input" />
            <Button
              variant="outline"
              onClick={() => setShareOpen(true)}
              className="rounded-full h-11 bg-white"
              data-testid="share-btn"
            >
              <Share2 className="w-4 h-4 mr-2" /> Share
            </Button>
            <Button
              variant="outline"
              onClick={() => setResumeOpen(true)}
              className="rounded-full h-11 bg-white"
              data-testid="resume-import-btn"
            >
              <FileText className="w-4 h-4 mr-2 text-[#D99C3D]" /> From resume
            </Button>
            <Button
              variant="outline"
              onClick={() => setParseJdOpen(true)}
              className="rounded-full h-11 bg-white"
              data-testid="paste-jd-btn"
            >
              <Sparkles className="w-4 h-4 mr-2 text-[#D99C3D]" /> Paste JD {!isPro && <Lock className="w-3 h-3 ml-1.5 opacity-60" />}
            </Button>
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={importing}
              className="rounded-full h-11 bg-white" data-testid="import-csv-btn">
              <Upload className="w-4 h-4 mr-2" /> {importing ? "Importing…" : "Import CSV"}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="rounded-full h-11 bg-white" data-testid="export-menu-trigger">
                  <Download className="w-4 h-4 mr-2" /> Export <ChevronDown className="w-4 h-4 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white">
                <DropdownMenuItem onClick={() => doExport("csv")} data-testid="export-csv">CSV (.csv)</DropdownMenuItem>
                <DropdownMenuItem onClick={() => doExport("xlsx")} data-testid="export-xlsx">
                  Excel (.xlsx) {!isPro && <Lock className="w-3 h-3 ml-2 opacity-60" />}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => doExport("pdf")} data-testid="export-pdf">
                  PDF (.pdf) {!isPro && <Lock className="w-3 h-3 ml-2 opacity-60" />}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              onClick={() => {
                if (atFreeLimit) {
                  navigate("/upgrade");
                  return;
                }
                setEditing(null); setOpen(true);
              }}
              className="rounded-full h-11 bg-[#1A362D] hover:bg-[#2C4A40] text-white px-5"
              data-testid="add-application-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> {atFreeLimit ? "Upgrade to add more" : "New application"}
            </Button>
          </div>
        </div>

        {/* Free-tier limit banner */}
        {!isPro && apps.length >= 20 && (
          <div className="mt-6 rounded-2xl bg-[#1A362D] text-[#F4F1EE] p-5 flex items-center justify-between gap-4" data-testid="free-limit-banner">
            <div className="flex items-start gap-3">
              <Zap className="w-5 h-5 text-[#D99C3D] shrink-0 mt-0.5" />
              <div>
                <p className="font-display text-lg">
                  {atFreeLimit ? "You've hit the free plan limit" : `${25 - apps.length} of 25 applications left on Free`}
                </p>
                <p className="text-sm opacity-80 mt-0.5">
                  Trackr Pro is unlimited — plus Kanban, Analytics, Excel & PDF.
                </p>
              </div>
            </div>
            <Button
              onClick={() => navigate("/upgrade")}
              className="rounded-full bg-[#D99C3D] hover:bg-[#C48A2E] text-[#1A362D] font-semibold shrink-0"
              data-testid="banner-upgrade-btn"
            >
              Upgrade →
            </Button>
          </div>
        )}

        {/* Upcoming interviews banner */}
        {upcoming.length > 0 && (
          <div className="mt-8 rounded-2xl bg-[#FDF2DF] border border-[#D99C3D]/30 p-5" data-testid="upcoming-banner">
            <div className="flex items-start gap-3">
              <CalendarClock className="w-5 h-5 text-[#8B6A1F] shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-display text-lg text-[#1A362D]">
                  {upcoming.length} interview{upcoming.length === 1 ? "" : "s"} in the next 2 weeks
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {upcoming.slice(0, 5).map((a) => {
                    const d = daysUntil(a.interview_date);
                    const label = d === 0 ? "today" : d === 1 ? "tomorrow" : `in ${d} days`;
                    return (
                      <button
                        key={a.id}
                        onClick={() => { setEditing(a); setOpen(true); }}
                        className="text-sm rounded-full bg-white px-3 py-1.5 border border-[#D99C3D]/30 hover:border-[#1A362D] hover:shadow-sm transition-all text-[#1A362D]"
                        data-testid={`upcoming-chip-${a.id}`}
                      >
                        <span className="font-medium">{a.company}</span>
                        <span className="text-[#5C6B64]"> · {a.job_title} · {label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Follow-up needed banner */}
        {followupList.length > 0 && (
          <div className="mt-4 rounded-2xl bg-[#FCECEC] border border-[#C65D47]/30 p-5" data-testid="followup-banner">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-[#C65D47] shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-display text-lg text-[#1A362D]">
                  {followupList.length} application{followupList.length === 1 ? "" : "s"} could use a follow-up
                </p>
                <p className="text-sm text-[#5C6B64] mt-0.5">
                  No contact in 7+ days. A short, polite nudge often reopens the door.
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => setFollowupOnly((v) => !v)}
                    className={`text-sm rounded-full px-3 py-1.5 transition-colors ${
                      followupOnly
                        ? "bg-[#1A362D] text-white"
                        : "bg-white border border-[#C65D47]/30 text-[#1A362D] hover:border-[#1A362D]"
                    }`}
                    data-testid="followup-toggle-btn"
                  >
                    {followupOnly ? "Showing only follow-ups · clear" : "Show only follow-ups"}
                  </button>
                  {followupList.slice(0, 3).map((a) => (
                    <button
                      key={a.id}
                      onClick={() => { setEditing(a); setOpen(true); }}
                      className="text-sm rounded-full bg-white px-3 py-1.5 border border-[#C65D47]/30 hover:border-[#1A362D] hover:shadow-sm transition-all text-[#1A362D]"
                      data-testid={`followup-chip-${a.id}`}
                    >
                      <span className="font-medium">{a.company}</span>
                      <span className="text-[#5C6B64]"> · {a.job_title}</span>
                    </button>
                  ))}
                  {followupList.length > 3 && (
                    <span className="text-xs text-[#5C6B64]">+{followupList.length - 3} more</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Resume import report card */}
        {importSummary && (
          <div
            className="mt-4 rounded-2xl bg-[#E2F0E7] border border-[#1A362D]/15 p-5 flex items-start gap-3"
            data-testid="import-report-card"
          >
            <div className="w-10 h-10 rounded-full bg-[#1A362D] text-[#D99C3D] flex items-center justify-center shrink-0">
              <Sparkles className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-display text-lg text-[#1A362D]" data-testid="import-report-headline">
                {importSummary.created > 0
                  ? `${importSummary.created} role${importSummary.created === 1 ? "" : "s"} imported from your resume`
                  : "Nothing new to import"}
              </p>
              <div className="mt-1 text-sm text-[#5C6B64] flex flex-wrap gap-x-4 gap-y-1">
                {importSummary.created > 0 && (
                  <span className="inline-flex items-center gap-1" data-testid="import-report-created">
                    <Check className="w-3.5 h-3.5 text-[#1A362D]" /> {importSummary.created} added
                  </span>
                )}
                {importSummary.duplicates > 0 && (
                  <span className="inline-flex items-center gap-1" data-testid="import-report-duplicates">
                    <FileText className="w-3.5 h-3.5 text-[#8B8378]" /> {importSummary.duplicates} already tracked
                  </span>
                )}
                {importSummary.limit_reached &&
                  importSummary.skipped > importSummary.duplicates && (
                    <span className="inline-flex items-center gap-1 text-[#C65D47]" data-testid="import-report-skipped">
                      <Lock className="w-3.5 h-3.5" />
                      {importSummary.skipped - importSummary.duplicates} skipped · free plan limit
                    </span>
                  )}
              </div>
              {importSummary.limit_reached && (
                <button
                  onClick={() => navigate("/upgrade")}
                  className="mt-3 inline-flex items-center gap-1.5 text-sm rounded-full bg-[#1A362D] text-white px-3 py-1.5 hover:bg-[#2C4A40] transition-colors"
                  data-testid="import-report-upgrade-btn"
                >
                  <Zap className="w-3.5 h-3.5 text-[#D99C3D]" /> Upgrade to add the rest
                </button>
              )}
            </div>
            <button
              onClick={() => setImportSummary(null)}
              className="text-[#5C6B64] hover:text-[#1A362D] transition-colors shrink-0"
              aria-label="Dismiss"
              data-testid="import-report-dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-8">
          {[
            { label: "Total", value: stats.total, tint: "#1A362D", testid: "stat-total" },
            { label: "Applied", value: stats.applied, tint: "#5C6B64", testid: "stat-applied" },
            { label: "Interviewing", value: stats.interviewing, tint: "#D99C3D", testid: "stat-interviewing" },
            { label: "Offers", value: stats.offer, tint: "#1A362D", testid: "stat-offer" },
            { label: "Rejected", value: stats.rejected, tint: "#C65D47", testid: "stat-rejected" },
          ].map((s) => (
            <div key={s.testid} data-testid={s.testid}
              className="rounded-2xl bg-white border border-[#E5E1DB] p-5 transition-transform hover:-translate-y-0.5 hover:shadow-lg hover:shadow-black/5">
              <p className="text-xs tracking-[0.2em] uppercase text-[#5C6B64]">{s.label}</p>
              <p className="font-display text-4xl mt-2" style={{ color: s.tint }}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Toolbar */}
        <div className="mt-10 flex flex-col md:flex-row gap-3 md:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6B64]" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search company, title, source, notes…"
              className="h-11 pl-10 bg-white"
              data-testid="search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-11 md:w-48 bg-white" data-testid="filter-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All statuses</SelectItem>
              <SelectItem value="Applied">Applied</SelectItem>
              <SelectItem value="Interviewing">Interviewing</SelectItem>
              <SelectItem value="Offer">Offer</SelectItem>
              <SelectItem value="Rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
          <div className="inline-flex rounded-full border border-[#E5E1DB] bg-white p-1 h-11">
            <button
              onClick={() => setView("table")}
              className={`px-4 rounded-full text-sm inline-flex items-center gap-1.5 transition-colors ${
                view === "table" ? "bg-[#1A362D] text-white" : "text-[#5C6B64] hover:text-[#1A362D]"
              }`}
              data-testid="view-table-btn"
            >
              <TableIcon className="w-4 h-4" /> Table
            </button>
            <button
              onClick={() => {
                if (!isPro) {
                  toast.error("Kanban board is a Trackr Pro feature.", {
                    action: { label: "Upgrade", onClick: () => navigate("/upgrade") },
                  });
                  return;
                }
                setView("kanban");
              }}
              className={`px-4 rounded-full text-sm inline-flex items-center gap-1.5 transition-colors ${
                view === "kanban" ? "bg-[#1A362D] text-white" : "text-[#5C6B64] hover:text-[#1A362D]"
              }`}
              data-testid="view-kanban-btn"
            >
              <LayoutGrid className="w-4 h-4" /> Kanban {!isPro && <Lock className="w-3 h-3 opacity-60" />}
            </button>
          </div>
        </div>

        {/* Bulk action bar */}
        {selected.size > 0 && view === "table" && (
          <div className="mt-4 rounded-full bg-[#1A362D] text-white px-5 py-3 flex flex-wrap items-center gap-3 shadow-lg shadow-black/10" data-testid="bulk-bar">
            <span className="text-sm">
              <span className="font-display text-lg mr-1" data-testid="bulk-count">{selected.size}</span>
              selected
            </span>
            <div className="h-4 w-px bg-white/20" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="rounded-full text-white hover:bg-white/10 hover:text-white" data-testid="bulk-status-trigger">
                  Move to… <ChevronDown className="w-3.5 h-3.5 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="bg-white">
                <DropdownMenuLabel>Change status</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {["Applied", "Interviewing", "Offer", "Rejected"].map((s) => (
                  <DropdownMenuItem key={s} onClick={() => handleBulkStatus(s)} data-testid={`bulk-status-${s.toLowerCase()}`}>
                    {s}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="ghost" size="sm" onClick={() => setConfirmBulk(true)}
              className="rounded-full text-white hover:bg-[#C65D47]/40 hover:text-white" data-testid="bulk-delete-btn">
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
            </Button>
            <Button variant="ghost" size="sm" onClick={clearSelection}
              className="rounded-full text-white/80 hover:bg-white/10 hover:text-white ml-auto" data-testid="bulk-clear-btn">
              Clear
            </Button>
          </div>
        )}

        {/* Content */}
        {view === "kanban" ? (
          <div className="mt-6">
            <KanbanBoard
              apps={filtered}
              onEdit={(a) => { setEditing(a); setOpen(true); }}
              onDelete={(a) => setToDelete(a)}
              onStatusChange={handleKanbanStatus}
            />
          </div>
        ) : (
          <div className="mt-6 rounded-2xl bg-white border border-[#E5E1DB] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1200px]" data-testid="applications-table">
                <thead>
                  <tr className="text-left text-xs tracking-[0.15em] uppercase text-[#5C6B64] border-b border-[#E5E1DB] bg-[#FDFCFA]">
                    <th className="pl-5 pr-2 py-3.5 w-8">
                      <Checkbox
                        checked={allSelected || (someSelected ? "indeterminate" : false)}
                        onCheckedChange={toggleSelectAll}
                        data-testid="select-all-checkbox"
                      />
                    </th>
                    <th className="px-5 py-3.5"><SortHeader col={SORT_COLS[0]} /></th>
                    <th className="px-5 py-3.5"><SortHeader col={SORT_COLS[1]} /></th>
                    <th className="px-5 py-3.5"><SortHeader col={SORT_COLS[2]} /></th>
                    <th className="px-5 py-3.5">Link</th>
                    <th className="px-5 py-3.5"><SortHeader col={SORT_COLS[3]} /></th>
                    <th className="px-5 py-3.5"><SortHeader col={SORT_COLS[4]} /></th>
                    <th className="px-5 py-3.5"><SortHeader col={SORT_COLS[5]} /></th>
                    <th className="px-5 py-3.5"><SortHeader col={SORT_COLS[6]} /></th>
                    <th className="px-5 py-3.5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={10} className="px-5 py-14 text-center text-[#5C6B64]" data-testid="apps-loading">Loading…</td></tr>
                  ) : filtered.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="px-5 py-16 text-center" data-testid="apps-empty">
                        <div className="max-w-sm mx-auto">
                          <p className="font-display text-2xl text-[#1A362D]">No applications yet</p>
                          <p className="text-[#5C6B64] mt-2">
                            {apps.length === 0
                              ? "Log your first application or import a CSV to get started."
                              : "No results match your filters."}
                          </p>
                          {apps.length === 0 && (
                            <div className="mt-5 inline-flex flex-wrap justify-center gap-2">
                              <Button
                                onClick={() => { setEditing(null); setOpen(true); }}
                                className="rounded-full bg-[#1A362D] hover:bg-[#2C4A40] text-white"
                                data-testid="empty-add-btn"
                              >
                                <Plus className="w-4 h-4 mr-2" /> Add
                              </Button>
                              <Button
                                variant="outline"
                                onClick={() => setResumeOpen(true)}
                                className="rounded-full"
                                data-testid="empty-resume-btn"
                              >
                                <Sparkles className="w-4 h-4 mr-2 text-[#D99C3D]" /> From resume
                              </Button>
                              <Button
                                variant="outline"
                                onClick={() => fileRef.current?.click()}
                                className="rounded-full"
                                data-testid="empty-import-btn"
                              >
                                <Upload className="w-4 h-4 mr-2" /> Import CSV
                              </Button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filtered.map((a) => (
                      <tr key={a.id}
                        className={`border-b border-[#F0EDE7] last:border-0 hover:bg-[#FDFCFA] transition-colors ${
                          selected.has(a.id) ? "bg-[#F4F1EE]" : ""
                        }`}
                        data-testid={`app-row-${a.id}`}>
                        <td className="pl-5 pr-2 py-4">
                          <Checkbox
                            checked={selected.has(a.id)}
                            onCheckedChange={() => toggleSelect(a.id)}
                            data-testid={`row-checkbox-${a.id}`}
                          />
                        </td>
                        <td className="px-5 py-4 font-medium text-[#1A362D]">
                          <div className="flex items-center gap-2">
                            <span>{a.company}</span>
                            {followupIds.has(a.id) && (
                              <span
                                title="Follow-up needed — 7+ days since last contact"
                                className="inline-flex items-center gap-1 text-xs rounded-full bg-[#FCECEC] text-[#C65D47] px-2 py-0.5"
                                data-testid={`followup-badge-${a.id}`}
                              >
                                <AlertCircle className="w-3 h-3" /> Nudge
                              </span>
                            )}
                          </div>
                          {(a.recruiter_name || a.hiring_manager) && (
                            <div className="text-xs text-[#5C6B64] mt-0.5 inline-flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {a.recruiter_name || a.hiring_manager}
                            </div>
                          )}
                        </td>
                        <td className="px-5 py-4">
                          <div>{a.job_title}</div>
                          {a.status === "Interviewing" && a.interview_stage && (
                            <div className="text-xs text-[#8B6A1F] mt-0.5 inline-flex items-center gap-1">
                              <span className="w-1.5 h-1.5 rounded-full bg-[#D99C3D]" />
                              {a.interview_stage}
                            </div>
                          )}
                          {a.interview_date && (
                            <div className="text-xs text-[#8B6A1F] mt-0.5 inline-flex items-center gap-1">
                              <CalendarClock className="w-3 h-3" /> Interview {a.interview_date}
                            </div>
                          )}
                        </td>
                        <td className="px-5 py-4 text-[#5C6B64]">{a.website || "—"}</td>
                        <td className="px-5 py-4">
                          {a.job_url ? (
                            <a href={a.job_url} target="_blank" rel="noreferrer"
                              className="inline-flex items-center gap-1 text-[#1A362D] hover:text-[#C65D47]">
                              Open <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                          ) : <span className="text-[#5C6B64]">—</span>}
                        </td>
                        <td className="px-5 py-4 text-[#1A362D]">{a.pay_range || "—"}</td>
                        <td className="px-5 py-4">
                          <Badge className={`rounded-full ${STATUS_STYLE[a.status] || STATUS_STYLE.Applied} border-0`}>
                            {a.status}
                          </Badge>
                        </td>
                        <td className="px-5 py-4">
                          {a.submitted ? (
                            <span className="inline-flex items-center gap-1 text-[#1A362D]"><Check className="w-4 h-4" /> Yes</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[#5C6B64]"><X className="w-4 h-4" /> No</span>
                          )}
                        </td>
                        <td className="px-5 py-4 text-[#5C6B64]">{a.date_applied || "—"}</td>
                        <td className="px-5 py-4 text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="rounded-full h-8 w-8" data-testid={`row-menu-${a.id}`}>
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="bg-white">
                              <DropdownMenuItem onClick={() => { setEditing(a); setPrefill(null); setOpen(true); }} data-testid={`edit-${a.id}`}>
                                <Pencil className="w-4 h-4 mr-2" /> Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setCoverLetterFor(a)} data-testid={`cover-letter-${a.id}`}>
                                <FileText className="w-4 h-4 mr-2" /> Cover letter {!isPro && <Lock className="w-3 h-3 ml-2 opacity-60" />}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => setToDelete(a)} className="text-[#C65D47]" data-testid={`delete-${a.id}`}>
                                <Trash2 className="w-4 h-4 mr-2" /> Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      <ApplicationModal
        open={open}
        onOpenChange={(o) => { setOpen(o); if (!o) setPrefill(null); }}
        editing={editing}
        prefill={prefill}
        onSaved={load}
      />

      <ParseJDModal
        open={parseJdOpen}
        onOpenChange={setParseJdOpen}
        onParsed={(data) => {
          setEditing(null);
          setPrefill({
            company: data.company,
            job_title: data.job_title,
            pay_range: data.pay_range,
            website: data.website,
            job_url: data.job_url,
            notes: [data.notes, data.key_requirements ? `Key requirements: ${data.key_requirements}` : ""].filter(Boolean).join("\n\n"),
          });
          setParseJdOpen(false);
          setOpen(true);
        }}
      />

      <CoverLetterModal
        open={!!coverLetterFor}
        onOpenChange={(o) => !o && setCoverLetterFor(null)}
        application={coverLetterFor}
      />

      <ShareModal
        open={shareOpen}
        onOpenChange={setShareOpen}
      />

      <ResumeImportModal
        open={resumeOpen}
        onOpenChange={(o) => { setResumeOpen(o); if (!o) setResumeWelcome(false); }}
        welcome={resumeWelcome}
        onImported={(summary) => {
          load();
          if (summary) setImportSummary(summary);
          // Fire the 3-step tour once per user, right after their first import.
          if (user?.user_id) {
            const key = `trackr.tour.${user.user_id}`;
            if (!localStorage.getItem(key)) {
              localStorage.setItem(key, new Date().toISOString());
              setTourSteps(DEFAULT_TOUR_STEPS);
              // Delay so the modal close animation + table render settles first.
              setTimeout(() => setTourOpen(true), 450);
            }
          }
        }}
      />

      <WelcomeTour
        open={tourOpen}
        onClose={() => setTourOpen(false)}
        steps={tourSteps}
      />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">Delete application?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove {toDelete?.company} — {toDelete?.job_title}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full" data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}
              className="rounded-full bg-[#C65D47] hover:bg-[#B04D38] text-white"
              data-testid="delete-confirm-btn">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmBulk} onOpenChange={setConfirmBulk}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">Delete {selected.size} applications?</AlertDialogTitle>
            <AlertDialogDescription>
              This can't be undone. Consider exporting first.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full" data-testid="bulk-delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkDelete}
              className="rounded-full bg-[#C65D47] hover:bg-[#B04D38] text-white"
              data-testid="bulk-delete-confirm-btn">
              Delete all
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

